import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, Button, ProjectWorkflowStatusBadge } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProjectsListTable, type ProjectsListColumn } from '../components/ProjectsListTable';
import { useColumnOrder } from '../hooks/useColumnOrder';
import { lawyerApi } from '../services/api';
import { loadGlobalTemplate, projectDisplayStatusOptions, getStatusLabel as getStatusLabelUtil, type StatusStep } from '../services/projectStatusUtils';
import { projectDisplayStatus } from '../utils/milestoneWorkflowStatus';
import { useListSort } from '../hooks/useListSort';
import { DEFAULT_PROJECT_LIST_SORT, isCompletedProject, lastActivityColumn, projectListMobileSort } from '../utils/projectListSort';
import { formatLastActivity } from '../utils/lastActivity';
import { ProjectCompanyName } from '../components/ProjectCompanyName';

const COLUMN_ORDER: string[] = ['projectNumber', 'companyName', 'customerName', 'description', 'status', 'totalContractAmount', 'startDate', 'endDate', 'lastActivityAt'];
const PREFERENCE_KEY = 'lawyerProjectListColumnOrder';

/** The list, or with `completed` the list under "Avslutade Projekt": the same
 *  table with only the completed projects (ClickUp 869fc31mm). */
export function LawyerProjectList({ completed = false }: { completed?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [templateSteps, setTemplateSteps] = useState<StatusStep[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [columnOrder, handleReorder] = useColumnOrder(PREFERENCE_KEY, COLUMN_ORDER);
  const [listSort, handleListSort] = useListSort('lawyerProjectListSort', DEFAULT_PROJECT_LIST_SORT, COLUMN_ORDER);

  useEffect(() => {
    loadProjects();
    loadGlobalTemplate().then(setTemplateSteps).catch(() => {});
  }, []);

  const loadProjects = async () => {
    try {
      const data = await lawyerApi.getMyProjects();
      setProjects(data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // No "Avslutat" option: completed projects are listed under "Avslutade Projekt".
  const statusOptions = useMemo(
    () => projectDisplayStatusOptions(templateSteps).filter(option => option.value !== 'completed'),
    [templateSteps]
  );
  // The rows the table shows (filtered and sorted), read when exporting. A
  // ref, not state: the columns are rebuilt on every render, so state would
  // re-render in a loop.
  const visibleRowsRef = useRef<any[]>([]);
  const listedProjects = useMemo(
    () => projects.filter(project => isCompletedProject(project) === completed),
    [projects, completed]
  );
  const getStatusLabel = useCallback((status: string) => getStatusLabelUtil(status, templateSteps) || status, [templateSteps]);
  const formatAmount = (amount: number) => new Intl.NumberFormat('sv-SE').format(amount || 0);

  const openProject = (project: any) => navigate(`/lawyer/projects/${project.projectId ?? project.id}`);

  const statusBadge = (project: any) => (
    <ProjectWorkflowStatusBadge project={project} steps={templateSteps} />
  );

  const columns: ProjectsListColumn<any>[] = [
    { field: 'projectNumber', label: t('projects.columns.projectNumber'), render: p => `#${p.projectNumber}` },
    { field: 'companyName', label: t('providerRequests.contractorLabel'), render: p => <ProjectCompanyName companyName={p.companyName} /> },
    { field: 'customerName', label: t('projects.columns.customerName'), render: p => p.customerName || '—' },
    { field: 'description', label: t('projects.columns.description'), render: p => p.description },
    {
      field: 'status',
      label: t('projects.columns.status'),
      filter: completed ? 'none' : 'select',
      selectAllLabel: t('projects.allStatuses'),
      selectOptions: statusOptions,
      filterValue: p => projectDisplayStatus(p),
      sortValue: p => projectDisplayStatus(p),
      render: p => statusBadge(p),
    },
    { field: 'totalContractAmount', label: t('projects.columns.totalContractAmount'), filter: 'none', sortValue: p => p.totalContractAmount || 0, render: p => `${formatAmount(p.totalContractAmount)} kr` },
    { field: 'startDate', label: t('projects.columns.startDate'), render: p => p.startDate || '—' },
    { field: 'endDate', label: t('projects.columns.endDate'), render: p => p.endDate || '—' },
    lastActivityColumn<any>(t),
  ];

  const exportCsv = () => {
    const header = columns.map(c => c.label);
    const rows = visibleRowsRef.current.map(p => [
      p.projectNumber, p.companyName || '', p.customerName || '', p.description || '',
      getStatusLabel(projectDisplayStatus(p)), p.totalContractAmount || 0, p.startDate || '', p.endDate || '',
    ]);
    const csv = [header.join(';'), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';'))].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `projects_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const selectedProject = projects.find(p => p.id === selectedId);

  const renderMobileCard = (project: any, { open, selected }: { open: () => void; selected: boolean }) => (
    <div
      className={`card mb-2 ${selected ? 'border-primary' : ''}`}
      onClick={() => setSelectedId(prev => prev === project.id ? null : project.id)}
      onDoubleClick={open}
      style={{ cursor: 'pointer' }}
    >
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
              <span className="fw-semibold text-break">#{project.projectNumber} {project.description}</span>
              {statusBadge(project)}
            </div>
            {/* The parties by role: the Beställare and the hired Hantverkare (ClickUp 869fk6kz3). */}
            {project.customerName && (
              <div className="text-muted small text-break"><i className="feather-user me-1" aria-hidden="true"></i>{t('providerRequests.customerLabel')}: {project.customerName}</div>
            )}
            <div className="text-muted small text-break"><i className="feather-briefcase me-1" aria-hidden="true"></i>{t('providerRequests.contractorLabel')}: <ProjectCompanyName companyName={project.companyName} /></div>
            <div className="d-flex flex-wrap gap-3 mt-1">
              {(project.startDate || project.endDate) && (
                <span className="text-muted small"><i className="feather-calendar me-1"></i>{project.startDate || '—'} – {project.endDate || '—'}</span>
              )}
              <span className="text-muted small"><i className="feather-clock me-1" aria-hidden="true"></i>{t('projects.columns.lastActivityAt')}: {formatLastActivity(project.lastActivityAt, t)}</span>
              {project.totalContractAmount > 0 && (
                <span className="text-muted small"><i className="feather-tag me-1"></i>{formatAmount(project.totalContractAmount)} kr</span>
              )}
            </div>
          </div>
          <div className="d-flex gap-1 flex-shrink-0 ms-2">
            <button className="btn btn-sm btn-light-primary" onClick={(e) => { e.stopPropagation(); open(); }} title={t('lawyerProjects.goToProject', 'Gå till Projekt')}>
              <i className="feather-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{completed ? t('nav.completedProjects') : t('lawyerProjects.title', 'Projekt')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: completed ? t('nav.completedProjects') : t('lawyerProjects.title', 'Projekt') }
          ]} />
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      ) : listedProjects.length === 0 ? (
        <Card>
          {completed ? (
            <div className="text-center py-5 text-muted" data-testid="completed-projects-empty">
              <i className="feather-check-circle fs-1 d-block mb-3" aria-hidden="true"></i>
              <h5 className="fw-bold mb-0">{t('projects.completed.empty')}</h5>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="feather-inbox fs-1 d-block mb-3"></i>
              <h5 className="fw-bold mb-2">{t('lawyerProjects.noProjects', 'Inga projekt än')}</h5>
              <p className="mb-0">{t('lawyerProjects.noProjectsDesc', 'Du har inte blivit inbjuden till några projekt ännu.')}</p>
            </div>
          )}
        </Card>
      ) : (
        <Card>
          <ProjectsListTable
            rows={listedProjects}
            columns={columns}
            rowKey={p => p.id}
            columnOrder={columnOrder}
            onColumnReorder={handleReorder}
            reorderTitle={t('projects.dragToReorder')}
            sort={listSort}
            onSortChange={handleListSort}
            onOpen={openProject}
            onSelect={p => setSelectedId(prev => prev === p.id ? null : p.id)}
            selectedKey={selectedId}
            actionTitle={t('lawyerProjects.goToProject', 'Gå till Projekt')}
            emptyText={completed ? t('projects.completed.empty') : t('lawyerProjects.noProjects', 'Inga projekt än')}
            noResultsText={t('projects.noResults')}
            mobileFilter={{ searchField: 'description', select: completed ? undefined : { field: 'status', allLabel: t('projects.allStatuses'), options: statusOptions }, sort: projectListMobileSort(t) }}
            renderMobileCard={renderMobileCard}
            onVisibleRowsChange={rows => { visibleRowsRef.current = rows; }}
            toolbar={
              <>
                <Button variant="light-brand" onClick={() => selectedProject && openProject(selectedProject)} disabled={!selectedProject}>
                  <i className="feather-arrow-right"></i>
                  <span className="d-none d-sm-inline ms-2">{t('lawyerProjects.goToProject', 'Gå till Projekt')}</span>
                </Button>
                <Button variant="light-brand" onClick={exportCsv}>
                  <i className="feather-download"></i>
                  <span className="d-none d-sm-inline ms-2">{t('projects.export')}</span>
                </Button>
                {completed && (
                  <span className="projects-list-fixed-filter" data-testid="completed-projects-filter">
                    <i className="feather-check-circle" aria-hidden="true"></i>
                    {t('projects.completed.fixedFilter')}
                  </span>
                )}
              </>
            }
          />
        </Card>
      )}
    </DashboardLayout>
  );
}
